import { FC, useCallback, useMemo } from "react";
import { MenuItem } from "@mui/material";
import { ResourceType } from "@/services/cloudchipr.api";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { liveUsageMgmtAccountsVisibilitiesByResourceTypeSelector } from "@/store/live-usage-mgmt/selectors/accounts-data/liveUsageMgmtAccountsVisibilitiesByResourceTypeSelector";
import { CleanActionDetails } from "@/utils/clean-options";
import { useAppAbility } from "@/services/permissions";
import { selectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesByMasterIdSelector";
import { setResourceGridCurrentCleanDetails } from "@/store/resources-grid/resourcesGridSlice";
import { selectedResourcesResourceTypeByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesResourceTypeByMasterIdSelector";
import { cleanDisabledForSelectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/checkers/cleanDisabledForSelectedResourcesByMasterIdSelector";

interface LiveUsageResourceGridToolbarCleanActionItemProps {
  onClose?(): void;
  masterResourceType: ResourceType;
  masterGridId: string;
  item: CleanActionDetails;
}

export const LiveUsageResourceGridToolbarCleanActionItem: FC<
  LiveUsageResourceGridToolbarCleanActionItemProps
> = ({ masterResourceType, masterGridId, item, onClose }) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotCleanResource = cannot("clean", "resource");

  const selectedResources = useAppSelector((state) =>
    selectedResourcesByMasterIdSelector(state, masterGridId),
  );

  const selectedResourcesResourceType = useAppSelector((state) =>
    selectedResourcesResourceTypeByMasterIdSelector(state, masterGridId),
  );

  const cleanDisabled = useAppSelector((state) =>
    cleanDisabledForSelectedResourcesByMasterIdSelector(state, masterGridId),
  );

  const resourceVisibility = useAppSelector((state) =>
    liveUsageMgmtAccountsVisibilitiesByResourceTypeSelector(
      state,
      masterResourceType,
    ),
  );

  const onCleanItemClick = useCallback(() => {
    if (!selectedResourcesResourceType) {
      return;
    }

    dispatch(
      setResourceGridCurrentCleanDetails({
        masterGridId: masterGridId,
        cleanAction: item.value,
        resourceType: selectedResourcesResourceType,
      }),
    );

    onClose?.();
  }, [
    onClose,
    dispatch,
    item.value,
    masterGridId,
    selectedResourcesResourceType,
  ]);

  const isAllResourcesDisabledForSomeReason = useMemo(() => {
    return selectedResources?.every(
      (resource) =>
        !resource ||
        resource.is_protected ||
        resource.marked_for_deletion ||
        resource?.not_allowed_actions?.includes(item.value),
    );
  }, [selectedResources, item.value]);

  const itemDisabled =
    cleanDisabled ||
    canNotCleanResource ||
    !selectedResources?.length ||
    isAllResourcesDisabledForSomeReason ||
    resourceVisibility?.actions[item.value] === false;

  return (
    <MenuItem
      dense
      key={item.value}
      onClick={onCleanItemClick}
      disabled={itemDisabled}
    >
      {item.label}
    </MenuItem>
  );
};
